import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { User, Clock } from "lucide-react";
import { format, startOfWeek, endOfWeek } from "date-fns";

export default function AdminCoaches() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const [seasonId, setSeasonId] = useState("");

  const { data: seasons = [] } = useQuery({
    queryKey: ["coach-seasons", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("seasons")
        .select("id, name")
        .eq("organization_id", organization!.id)
        .order("start_date", { ascending: false });
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  if (!seasonId && seasons.length > 0) {
    setTimeout(() => setSeasonId(seasons[0].id), 0);
  }

  const { data: groups = [] } = useQuery({
    queryKey: ["coach-groups", seasonId, organization?.id],
    queryFn: async () => {
      // No FK join for profiles — coach_id FK points to auth.users, not profiles
      const { data: groupRows } = await supabase
        .from("training_groups")
        .select("id, name, color, coach_id, schedule")
        .eq("organization_id", organization!.id)
        .eq("season_id", seasonId)
        .order("name");
      if (!groupRows?.length) return [];

      // Fetch coach profiles separately
      const ids = [...new Set(groupRows.map((g) => g.coach_id).filter(Boolean))];
      // INTENTIONAL: bucket-C admin-PII read via "Admin all profiles" RLS policy.
      // Reads `phone` for org-admin coach contact list. Stays on public.profiles.
      const { data: profiles } = ids.length > 0
        ? await supabase.from("profiles").select("id, full_name, phone").in("id", ids)
        : { data: [] as { id: string; full_name: string; phone: string }[] };
      const profileMap = new Map((profiles ?? []).map((p) => [p.id, p]));

      return groupRows.map((g) => ({
        ...g,
        profiles: g.coach_id ? profileMap.get(g.coach_id) ?? null : null,
      }));
    },
    enabled: !!seasonId && !!organization?.id,
  });

  const coachIds = [...new Set(groups.map((g: Record<string, unknown>) => g.coach_id).filter(Boolean))];

  const now = new Date();
  const weekStart = format(startOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd");
  const weekEnd = format(endOfWeek(now, { weekStartsOn: 1 }), "yyyy-MM-dd");

  const { data: weekSessions = [] } = useQuery({
    queryKey: ["coach-week-sessions", organization?.id, weekStart, coachIds],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_sessions")
        .select("coach_id, session_date, start_time")
        .eq("organization_id", organization!.id)
        .in("coach_id", coachIds)
        .gte("session_date", weekStart)
        .lte("session_date", weekEnd)
        .neq("status", "cancelled");
      return data ?? [];
    },
    enabled: !!organization?.id && coachIds.length > 0,
  });

  const { data: totalSessions = [] } = useQuery({
    queryKey: ["coach-total-sessions", seasonId, organization?.id, coachIds],
    queryFn: async () => {
      const groupIds = groups.map((g: Record<string, unknown>) => g.id);
      if (groupIds.length === 0) return [];
      const { data } = await supabase
        .from("training_sessions")
        .select("coach_id, id")
        .eq("organization_id", organization!.id)
        .in("group_id", groupIds)
        .neq("status", "cancelled");
      return data ?? [];
    },
    enabled: !!organization?.id && !!seasonId && groups.length > 0,
  });

  // Group data per coach
  const coachMap = new Map<string, {
    name: string;
    phone: string | null;
    groups: { name: string; color: string | null; schedule: unknown }[];
    weekCount: number;
    totalCount: number;
  }>();

  groups.forEach((g: Record<string, unknown>) => {
    if (!g.coach_id) return;
    if (!coachMap.has(g.coach_id)) {
      coachMap.set(g.coach_id, {
        name: g.profiles?.full_name || t("coaches.unknown"),
        phone: g.profiles?.phone || null,
        groups: [],
        weekCount: 0,
        totalCount: 0,
      });
    }
    coachMap.get(g.coach_id)!.groups.push({
      name: g.name,
      color: g.color,
      schedule: g.schedule,
    });
  });

  weekSessions.forEach((s: Record<string, unknown>) => {
    if (coachMap.has(s.coach_id)) {
      coachMap.get(s.coach_id)!.weekCount++;
    }
  });

  totalSessions.forEach((s: Record<string, unknown>) => {
    if (s.coach_id && coachMap.has(s.coach_id)) {
      coachMap.get(s.coach_id)!.totalCount++;
    }
  });

  const coaches = Array.from(coachMap.entries()).sort((a, b) => a[1].name.localeCompare(b[1].name));

  const DAY_LABELS: Record<string, string> = {
    monday: t("coaches.dayMon"), tuesday: t("coaches.dayTue"), wednesday: t("coaches.dayWed"),
    thursday: t("coaches.dayThu"), friday: t("coaches.dayFri"), saturday: t("coaches.daySat"), sunday: t("coaches.daySun"),
  };

  return (
    <div className="space-y-4">
      <Select value={seasonId} onValueChange={setSeasonId}>
        <SelectTrigger className="w-[220px]">
          <SelectValue placeholder={t("coaches.selectSeason")} />
        </SelectTrigger>
        <SelectContent>
          {seasons.map((s: Record<string, unknown>) => (
            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {coaches.length === 0 && seasonId && (
        <p className="text-sm text-muted-foreground">{t("coaches.noCoachesAssigned")}</p>
      )}

      <div className="grid gap-3">
        {coaches.map(([id, coach]) => (
          <Card key={id}>
            <CardContent className="py-4 px-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{coach.name}</p>
                    {coach.phone && <p className="text-xs text-muted-foreground">{coach.phone}</p>}
                  </div>
                </div>
                <div className="flex gap-3 text-center">
                  <div>
                    <p className="text-lg font-bold">{coach.weekCount}</p>
                    <p className="text-[10px] text-muted-foreground">{t("coaches.thisWeek")}</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">{coach.totalCount}</p>
                    <p className="text-[10px] text-muted-foreground">{t("coaches.total")}</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {coach.groups.map((g, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    {g.color && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: g.color }} />}
                    <Badge variant="outline" className="text-xs">{g.name}</Badge>
                  </div>
                ))}
              </div>

              {/* Schedule summary */}
              <div className="flex flex-wrap gap-1.5">
                {coach.groups.flatMap((g) => {
                  const sched = Array.isArray(g.schedule) ? g.schedule : [];
                  return sched.map((slot: Record<string, unknown>, si: number) => (
                    <Badge key={`${g.name}-${si}`} variant="secondary" className="text-[10px] gap-1">
                      <Clock className="h-3 w-3" />
                      {DAY_LABELS[slot.day] || slot.day} {slot.start_time?.slice(0, 5)}
                      <span className="text-muted-foreground">({g.name})</span>
                    </Badge>
                  ));
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
